import type { Project, ProjectDeadlines } from '@/api/client';
import { getDeadlineDaysLeft } from '@/utils/projectDeadlineRemaining';
import { PROJECT_LIST_SORTS, type ProjectListSort } from '@projectSearch/searchSort';
import { getEffectiveDeadline, type ProjectStage } from '@stages/stages';

/**
 * Порядок списка «В работе» и поиска (концепт compact-rows, раунд 3–4): группы по этапам, по срочности или один список
 * по алфавиту. Контракт общий с сервером — поиск сортирует он.
 */
export const LIST_SORTS = PROJECT_LIST_SORTS;

export type ListQueryView = 'loading' | 'error' | 'data';

/**
 * Что показать вместо списка: ошибка — только когда показывать нечего. Неудачный фоновый перезапрос
 * (по событию проекта) оставляет в запросе data и ставит isError — найденное остаётся на экране.
 */
export function listQueryView({ isPending, isError, hasData }: { isPending: boolean; isError: boolean; hasData: boolean }): ListQueryView {
  if (hasData) return 'data';
  if (isError) return 'error';
  return isPending ? 'loading' : 'data';
}

export type ListSort = ProjectListSort;

export const LIST_SORT_LABEL: Record<ListSort, string> = {
  stage: 'По этапам',
  urgency: 'По срочности',
  alpha: 'По алфавиту',
};

/** Вид строк: карточками (обложка и три строки) или в одну строку (5.1.1). */
export const LIST_VIEWS = ['cards', 'lines'] as const;
export type ListView = (typeof LIST_VIEWS)[number];

export type UrgencyKey = 'overdue' | 'week' | 'twoWeeks' | 'later' | 'none';

export interface UrgencyGroup<T> {
  key: UrgencyKey;
  label: string;
  /** Цвет корешка — пороги те же, что у срока в строке: до 7 дней красный, до 14 — оранжевый, дальше серый */
  color: string;
  items: T[];
}

const URGENCY: readonly { key: UrgencyKey; label: string; color: string; test: (days: number | null) => boolean }[] = [
  { key: 'overdue', label: 'Просрочено', color: 'var(--destructive)', test: (d) => d !== null && d < 0 },
  { key: 'week', label: 'Эта неделя', color: 'var(--destructive)', test: (d) => d !== null && d >= 0 && d <= 7 },
  { key: 'twoWeeks', label: 'Две недели', color: 'var(--warn)', test: (d) => d !== null && d > 7 && d <= 14 },
  { key: 'later', label: 'Позже', color: 'var(--stage-added)', test: (d) => d !== null && d > 14 },
  { key: 'none', label: 'Без срока', color: 'var(--stage-added)', test: (d) => d === null },
];

/** Проект со сроками и этапом — всё, из чего выбирается срок строки списка */
export type ListDeadlineSource = { state?: string | null } & ProjectDeadlines;

export interface ListDeadline {
  /** Дата «ГГГГ-ММ-ДД» */
  value: string;
  /** «Завершить запись до» */
  label: string;
  /** Текущий этап проекта */
  stage: ProjectStage;
  /** Срок этапа (перед остатком — квадратик этапа), а не общий «Завершить производство до» */
  isStageDeadline: boolean;
}

/** Срок строки списка и сортировки «По срочности»: срок текущего этапа, если задан, иначе общий (общий контракт этапов, как на сервере). */
export function getListDeadline(project: ListDeadlineSource): ListDeadline | null {
  const effective = getEffectiveDeadline(project);
  if (!effective) return null;
  return { value: effective.value, label: effective.label, stage: effective.stage, isStageDeadline: effective.field !== 'deadline' };
}

/** Группы по срочности (срок — getListDeadline): внутри — от самого горящего; пустые группы отбрасываются. */
export function groupByUrgency<T extends ListDeadlineSource>(items: readonly T[]): UrgencyGroup<T>[] {
  const withDays = items.map((item) => ({ item, days: getDeadlineDaysLeft(getListDeadline(item)?.value) }));
  return URGENCY.map(({ key, label, color, test }) => ({
    key,
    label,
    color,
    items: withDays
      .filter((x) => test(x.days))
      .sort((a, b) => (a.days ?? 0) - (b.days ?? 0))
      .map((x) => x.item),
  })).filter((g) => g.items.length > 0);
}

/**
 * Порядок внутри группы этапа: по сроку строки (getListDeadline — срок текущего этапа, иначе общий), просроченные
 * и ближайшие сверху, без срока в конце. Равные — в прежнем порядке (сервер: свежие правки сверху). Сервер сортирует так же
 * (projectSearchOrderBySql 'stage'), а здесь порядок пересчитывается сразу после правки срока, без перезапроса.
 */
export function sortByDeadline<T extends ListDeadlineSource>(items: readonly T[]): T[] {
  return items
    .map((item) => ({ item, days: getDeadlineDaysLeft(getListDeadline(item)?.value) }))
    // Оба без срока: Infinity − Infinity = NaN — считаются равными
    .sort((a, b) => (a.days ?? Infinity) - (b.days ?? Infinity) || 0)
    .map((x) => x.item);
}

const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true });

/** Один список по названию, А–Я. */
export function sortByName<T extends Pick<Project, 'name'>>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => collator.compare(a.name.trim(), b.name.trim()));
}
